import { SidebarSection, GamerSection, AnySidebarSection } from '../types/navigation'
import { FacilitatorRoutes, GamerRoutes, SharedRoutes } from '../../config/routes/routeEnums'
import config from '../../Config'

export const resolveBackOfficeDashboardUrl = (environmentName = config.name) =>
  environmentName === 'development' || environmentName === 'local'
    ? 'https://intraction-bo-dev.web.app/dashboard'
    : 'https://intraction-bo.web.app/dashboard'

/** The attribute a sidebar entry carries with its section id, so the platform tour can light it up. */
export const SIDEBAR_SECTION_ATTRIBUTE = 'data-sidebar-section'

/**
 * Maps sidebar sections to their corresponding URLs
 */
export const SIDEBAR_SECTION_URLS: Record<SidebarSection, string> = {
  [SidebarSection.DASHBOARD_FACILITATOR]: FacilitatorRoutes.BASE,
  [SidebarSection.TRAINING_SPRINT_MANAGEMENT]: FacilitatorRoutes.TRAINING_SPRINT_MANAGEMENT,
  [SidebarSection.SPRINT_CREATION]: FacilitatorRoutes.SPRINT_CREATION,
  [SidebarSection.DOWNLOAD_REPORTS]: FacilitatorRoutes.MANAGE_REPORTS,
  [SidebarSection.SESSIONS]: FacilitatorRoutes.SESSIONS,
  [SidebarSection.REPORTS]: FacilitatorRoutes.MANAGE_REPORTS,
  [SidebarSection.LICENSES]: '/facilitator/licenses',
  [SidebarSection.GUIDES]: '/facilitator/guides',
  [SidebarSection.QA]: FacilitatorRoutes.QA,
  [SidebarSection.EXPERT_VIDEOS]: '/facilitator/expert-videos',
  [SidebarSection.PROFILE]: FacilitatorRoutes.PROFILE,
  [SidebarSection.USERS]: resolveBackOfficeDashboardUrl(),
  [SidebarSection.GAME_CENTER_FACILITATOR]: '/facilitator/game-center-facilitator',
  [SidebarSection.REFLECTIONS]: '/facilitator/manage-reflections',
  [SidebarSection.ORGANIZATION_MEMBERS]: FacilitatorRoutes.ORGANIZATION_MEMBERS,
  [SidebarSection.ORGANIZATION_INVITES]: FacilitatorRoutes.ORGANIZATION_INVITES,
  [SidebarSection.ORGANIZATION_SETTINGS]: FacilitatorRoutes.ORGANIZATION_SETTINGS
}

/**
 * Maps gamer sections to their corresponding URLs
 */
export const GAMER_SECTION_URLS: Record<GamerSection, string> = {
  [GamerSection.DASHBOARD_GAMER]: GamerRoutes.DASHBOARD,
  [GamerSection.MY_PEOPLE]: GamerRoutes.MY_PEOPLE,
  [GamerSection.LATEST_SESSION_FEEDBACK]: GamerRoutes.LATEST_SESSION_FEEDBACK,
  [GamerSection.UPCOMING_ACTIVITIES]: GamerRoutes.UPCOMING_ACTIVITIES,
  [GamerSection.ONBOARDING]: GamerRoutes.ONBOARDING,
  [GamerSection.ARIA_COACH]: GamerRoutes.ARIA_COACH,
  // SharedRoutes.GAME_CENTER is the unprefixed '/game-center'; inside the gamer shell it is nested.
  [GamerSection.GAME_CENTER]: `/gamer${SharedRoutes.GAME_CENTER}`,
  [GamerSection.MY_PROFILE]: GamerRoutes.MY_PROFILE,
  [GamerSection.FAQ]: GamerRoutes.FAQ,
  [GamerSection.ACCOUNT_SETTINGS]: GamerRoutes.ACCOUNT_SETTINGS,
  [GamerSection.ACTIVITY_DETAILS]: GamerRoutes.ACTIVITY_DETAILS,
  [GamerSection.DEEPER_TEAM_ANALYSIS]: GamerRoutes.DEEPER_TEAM_ANALYSIS,
  [GamerSection.DEEPER_INDIVIDUAL_ANALYSIS]: GamerRoutes.DEEPER_INDIVIDUAL_ANALYSIS,
  [GamerSection.ACHIEVEMENTS]: GamerRoutes.ACHIEVEMENTS,
  [GamerSection.ALL_UPCOMING_ACTIVITIES]: GamerRoutes.ALL_UPCOMING_ACTIVITIES
}

/**
 * Gets the URL for a given sidebar section (facilitator or gamer)
 */
export const getUrlForSidebarSection = (section: AnySidebarSection): string => {
  if (Object.values(GamerSection).includes(section as GamerSection)) {
    return GAMER_SECTION_URLS[section as GamerSection]
  }
  return SIDEBAR_SECTION_URLS[section as SidebarSection]
}

/**
 * Gets the sidebar section for a given URL path (facilitator or gamer)
 */
export const getSidebarSectionFromUrl = (pathname: string): AnySidebarSection | null => {
  // Handle gamer routes first
  if (pathname.startsWith('/gamer')) {
    if (pathname.includes('/latest-session-feedback')) {
      return GamerSection.LATEST_SESSION_FEEDBACK
    }
    // The call of the whole sprint is one of the upcoming activities.
    const isUpcomingActivitiesPath =
      pathname.includes(GamerRoutes.UPCOMING_ACTIVITIES) || pathname.startsWith(GamerRoutes.SPRINT_CALL)

    if (isUpcomingActivitiesPath) {
      return GamerSection.UPCOMING_ACTIVITIES
    }
    if (pathname.includes('/onboarding')) {
      return GamerSection.ONBOARDING
    }
    if (pathname.includes('/play-game-with-introduction')) {
      return GamerSection.ONBOARDING
    }
    if (pathname.includes('/game-center')) {
      return GamerSection.GAME_CENTER
    }
    if (pathname.includes('/my-profile')) {
      return GamerSection.MY_PROFILE
    }
    if (pathname.includes('/account-settings')) {
      return GamerSection.ACCOUNT_SETTINGS
    }
    if (pathname.includes('/faq')) {
      return GamerSection.FAQ
    }

    // Check for achievements route - map to MY_PROFILE for sidebar active state
    // since users navigate to achievements from MyProfile page
    if (
      pathname.includes('/achievements') &&
      !pathname.includes('/dev-tools') &&
      !pathname.includes('/unseen-achievements')
    ) {
      return GamerSection.MY_PROFILE
    }
    // Check for unseen achievements route - map to DASHBOARD_GAMER for sidebar active state
    if (pathname.includes('/unseen-achievements') && !pathname.includes('/dev-tools')) {
      return GamerSection.DASHBOARD_GAMER
    }
    // Check for AI Coach route - map to DASHBOARD_GAMER for sidebar active state (similar to game-center)
    if (pathname.includes('/ai-coach') && !pathname.includes('/dev-tools')) {
      return GamerSection.DASHBOARD_GAMER
    }

    // Check for exact matches in gamer sections
    for (const [section, url] of Object.entries(GAMER_SECTION_URLS)) {
      if (pathname === url) {
        return section as GamerSection
      }
    }

    // Default to gamer dashboard
    return GamerSection.DASHBOARD_GAMER
  }

  // Handle facilitator specific routes
  if (pathname.includes(FacilitatorRoutes.PROFILE)) {
    return SidebarSection.PROFILE
  }
  if (
    pathname.includes(FacilitatorRoutes.ORGANIZATION_MEMBERS) ||
    pathname.includes(FacilitatorRoutes.ORGANIZATION_INVITES)
  ) {
    return SidebarSection.ORGANIZATION_MEMBERS
  }
  if (pathname.includes(FacilitatorRoutes.ORGANIZATION_SETTINGS)) {
    return SidebarSection.ORGANIZATION_SETTINGS
  }
  if (pathname.includes('/facilitator/game-center-facilitator')) {
    return SidebarSection.GAME_CENTER_FACILITATOR
  }
  if (pathname.includes(FacilitatorRoutes.SPRINT_CREATION)) {
    return SidebarSection.TRAINING_SPRINT_MANAGEMENT
  }
  if (pathname.includes('/facilitator/sprint/')) {
    return SidebarSection.TRAINING_SPRINT_MANAGEMENT
  }
  if (pathname.includes(FacilitatorRoutes.USERS)) {
    return SidebarSection.USERS
  }
  if (pathname.includes('/dev-tools')) {
    return SidebarSection.PROFILE // Dev tools are under profile
  }

  // Handle reports routes - both manage-reports and generate-report map to DOWNLOAD_REPORTS section
  // Also handle report overview pages (e.g., /facilitator/generate-report/report/:reportId)
  if (
    pathname.includes(FacilitatorRoutes.MANAGE_REPORTS) ||
    pathname.includes(FacilitatorRoutes.GENERATE_REPORT) ||
    pathname.includes('/facilitator/generate-report/report/')
  ) {
    return SidebarSection.DOWNLOAD_REPORTS
  }

  // Reflections: wizard, detail, and list share one sidebar item (same pattern as reports)
  // Route template uses `:reflectionId` — match the real path prefix, not the param placeholder.
  if (
    pathname.includes('/facilitator/facilitate-reflection') ||
    pathname.includes(FacilitatorRoutes.MANAGE_REFLECTIONS)
  ) {
    return SidebarSection.REFLECTIONS
  }

  // Handle exact matches for facilitator sections
  for (const [section, url] of Object.entries(SIDEBAR_SECTION_URLS)) {
    if (pathname === url) {
      return section as SidebarSection
    }
  }

  // Default fallback
  if (pathname.startsWith('/facilitator')) {
    return SidebarSection.DASHBOARD_FACILITATOR
  }

  return null
}
